import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { APIEndpoints } from "@/constants/apiEndpoints";
import { ENV_VARS } from "@/constants/env";
import { getAuthTokensFromLocalStorage } from "@/utils/auth";
import { formatBytes } from "@/utils/file.utils";
import { languageHeader } from "@/i18n/request-language";

export const MAYA_MAX_ATTACHMENTS = 10;
export const MAYA_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const MAYA_ACCEPTED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
  "application/pdf",
  // Spreadsheets — CSV / XLSX / XLS. MIME varies by OS/browser, so the
  // extension check in isAcceptedType is the reliable path for these.
  "text/csv",
  "application/csv",
  "application/vnd.ms-excel", // .xls and some .csv on Windows
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
];
// Extensions accepted regardless of (often unreliable) reported MIME type.
export const MAYA_ACCEPTED_EXTENSIONS = [".csv", ".xlsx", ".xls"];
export const MAYA_ACCEPT_ATTRIBUTE = "image/*,application/pdf,.csv,.xlsx,.xls";

export type AttachmentStatus = "uploading" | "done" | "failed";

export interface IMayaAttachment {
  id: string;
  file: File;
  name: string;
  status: AttachmentStatus;
  url?: string;
  error?: string;
}

interface UploadResponseItem {
  name?: string;
  url?: string;
}

interface UploadResponse {
  data?: UploadResponseItem | UploadResponseItem[];
}

const createId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto)
    return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const hasAcceptedExtension = (name: string) => {
  const lower = name.toLowerCase();
  return MAYA_ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

const isAcceptedType = (file: File) => {
  if (MAYA_ACCEPTED_MIME_TYPES.includes(file.type)) return true;
  // Any raster image — not SVG, which is a document that can carry script.
  if (file.type.startsWith("image/") && file.type !== "image/svg+xml")
    return true;
  // CSV/XLSX often arrive with empty or generic MIME — trust the extension.
  if (hasAcceptedExtension(file.name)) return true;
  return false;
};

export const useMayaFileUploads = (uploadPath?: string) => {
  const { t } = useTranslation(["common"]);
  const resolvedUploadPath =
    uploadPath ?? APIEndpoints.MySpace.AIAssistant.MayaFileUpload;
  const [attachments, setAttachments] = useState<IMayaAttachment[]>([]);
  const abortMapRef = useRef<Map<string, AbortController>>(new Map());

  const updateAttachment = useCallback(
    (id: string, patch: Partial<IMayaAttachment>) => {
      setAttachments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, ...patch } : a)),
      );
    },
    [],
  );

  const uploadBatch = useCallback(
    async (batch: IMayaAttachment[]) => {
      if (batch.length === 0) return;
      const controller = new AbortController();
      batch.forEach((a) => abortMapRef.current.set(a.id, controller));

      try {
        const baseUrl = ENV_VARS.API_INSTANCE.replace(/\/$/, "");
        const authTokens = getAuthTokensFromLocalStorage();
        const formData = new FormData();
        // All files sent in one multipart request under the same "files" field.
        batch.forEach((a) => formData.append("files", a.file, a.file.name));

        const response = await fetch(`${baseUrl}${resolvedUploadPath}`, {
          method: "POST",
          headers: {
            ...(authTokens
              ? { Authorization: `Bearer ${authTokens.accessToken}` }
              : {}),
            ...languageHeader(),
          },
          body: formData,
          signal: controller.signal,
        });

        if (!response.ok) {
          const err = (await response.json().catch(() => ({}))) as Record<
            string,
            unknown
          >;
          throw new Error(
            typeof err?.message === "string"
              ? err.message
              : t("common:ai_assistant.upload_failed_status", {
                  status: response.status,
                }),
          );
        }

        const json = (await response.json()) as UploadResponse;
        const rawData = json.data;
        // Backend returns results in the same order files were sent.
        const items: UploadResponseItem[] = Array.isArray(rawData)
          ? rawData
          : rawData
            ? [rawData]
            : [];

        batch.forEach((a, i) => {
          const item = items[i];
          const url = item?.url;
          const name = item?.name ?? a.file.name;
          if (!url) {
            updateAttachment(a.id, {
              status: "failed",
              error: t("common:ai_assistant.upload_missing_url"),
            });
            toast.error(
              t("common:ai_assistant.attachment_upload_no_url", {
                name: a.file.name,
              }),
            );
          } else {
            updateAttachment(a.id, { status: "done", url, name });
          }
        });
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        const message =
          (err as Error).message || t("common:ai_assistant.upload_failed");
        batch.forEach((a) => {
          updateAttachment(a.id, { status: "failed", error: message });
          toast.error(
            t("common:ai_assistant.attachment_upload_failed", {
              name: a.file.name,
              reason: message,
            }),
          );
        });
      } finally {
        batch.forEach((a) => abortMapRef.current.delete(a.id));
      }
    },
    [updateAttachment, resolvedUploadPath, t],
  );

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const incoming = Array.from(files);
      const currentCount = attachments.length;
      const remainingSlots = MAYA_MAX_ATTACHMENTS - currentCount;

      if (remainingSlots <= 0) {
        toast.error(
          t("common:ai_assistant.attachment_limit_reached", {
            max: MAYA_MAX_ATTACHMENTS,
          }),
        );
        return;
      }

      const accepted: IMayaAttachment[] = [];
      let truncated = false;
      for (const file of incoming) {
        if (accepted.length >= remainingSlots) {
          truncated = true;
          break;
        }
        if (!isAcceptedType(file)) {
          toast.error(
            t("common:ai_assistant.attachment_type_not_allowed", {
              name: file.name,
            }),
          );
          continue;
        }
        if (file.size > MAYA_MAX_FILE_SIZE_BYTES) {
          toast.error(
            t("common:ai_assistant.attachment_too_large", {
              name: file.name,
              size: formatBytes(MAYA_MAX_FILE_SIZE_BYTES, 0),
            }),
          );
          continue;
        }
        accepted.push({
          id: createId(),
          file,
          name: file.name,
          status: "uploading",
        });
      }

      if (truncated)
        toast.error(
          t("common:ai_assistant.attachments_truncated", {
            count: remainingSlots,
            max: MAYA_MAX_ATTACHMENTS,
          }),
        );
      if (accepted.length === 0) return;

      setAttachments((prev) => [...prev, ...accepted]);
      uploadBatch(accepted);
    },
    [attachments.length, uploadBatch, t],
  );

  const removeAttachment = useCallback((id: string) => {
    const controller = abortMapRef.current.get(id);
    controller?.abort();
    abortMapRef.current.delete(id);
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const retryAttachment = useCallback(
    (id: string) => {
      const target = attachments.find((a) => a.id === id);
      if (!target || target.status !== "failed") return;
      const patched = {
        ...target,
        status: "uploading" as const,
        error: undefined,
      };
      updateAttachment(id, { status: "uploading", error: undefined });
      uploadBatch([patched]);
    },
    [attachments, uploadBatch, updateAttachment],
  );

  const clear = useCallback(() => {
    abortMapRef.current.forEach((c) => c.abort());
    abortMapRef.current.clear();
    setAttachments([]);
  }, []);

  const hasUploading = attachments.some((a) => a.status === "uploading");
  const hasFailed = attachments.some((a) => a.status === "failed");
  const successUrls = attachments
    .filter((a) => a.status === "done" && a.url)
    .map((a) => a.url!);
  const successAttachments = attachments
    .filter((a) => a.status === "done" && a.url)
    .map((a) => ({ name: a.name, url: a.url! }));

  return {
    attachments,
    addFiles,
    removeAttachment,
    retryAttachment,
    clear,
    hasUploading,
    hasFailed,
    successUrls,
    successAttachments,
  };
};
